import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight, MessageCircle, BadgeCheck, HardHat, ShieldCheck, Sparkles } from "lucide-react";
import { useSEO } from "../../lib/seo";
import { BRAND } from "../../data/content";
import { getPageImages, getPageDesign } from "../../data/seoImages";
import { PRODUCT_SHOP_MAP, SOLUTION_SHOP_CATEGORIES } from "../../data/shopMapping";
import { I, SectionHeading, slugify } from "./sections/shared";
import HeroSection from "./sections/HeroSection";
import BenefitsSection from "./sections/BenefitsSection";
import UseCasesSection from "./sections/UseCasesSection";
import TestimonialSection from "./sections/TestimonialSection";
import GallerySection from "./sections/GallerySection";
import { SingleProductCTA, RecommendedProductsStrip } from "./sections/ShopCTASection";

/**
 * PremiumLandingPage — renders 41 product pages + 15 solution pages.
 * Each slug is hashed to a stable combination of hero/benefits/use-case
 * layout, real photography and testimonial (see data/seoImages.js) — so
 * every page renders a distinct composition, driven by its own content.
 */

const ACCENT_MAP = {
  amber: {
    radial: "radial-gradient(circle at 30% 20%, rgba(212, 175, 55, 0.22), transparent 55%)",
    ring: "from-amber-200/30 via-amber-400/20 to-transparent",
    icon: "text-gold",
    chip: "bg-amber-400/10 border-amber-400/30 text-amber-100",
  },
  violet: {
    radial: "radial-gradient(circle at 30% 20%, rgba(167, 139, 250, 0.22), transparent 55%)",
    ring: "from-violet-300/30 via-fuchsia-400/20 to-transparent",
    icon: "text-violet-300",
    chip: "bg-violet-400/10 border-violet-400/30 text-violet-100",
  },
  cyan: {
    radial: "radial-gradient(circle at 30% 20%, rgba(103, 232, 249, 0.20), transparent 55%)",
    ring: "from-cyan-200/30 via-teal-400/20 to-transparent",
    icon: "text-cyan-200",
    chip: "bg-cyan-400/10 border-cyan-400/30 text-cyan-100",
  },
};

export default function PremiumLandingPage({ slug, kind, content, onBook }) {
  const accent = ACCENT_MAP[content?.accent || "amber"];

  useSEO({
    title: content.title,
    description: content.intro,
    path: `/${kind}/${slug}`,
    keywords: content.keywords,
  });

  if (!content) return null;

  const images = getPageImages(slug);
  const design = getPageDesign(slug);

  return (
    <main data-testid={`landing-${kind}-${slug}`} className="bg-[var(--gx-bg)] text-white overflow-x-hidden">
      <HeroSection variant={design.hero} content={content} accent={accent} image={images[0]} onBook={onBook} />

      <BenefitsSection variant={design.benefits} content={content} accent={accent} />

      <UseCasesSection variant={design.usecase} content={content} images={images.slice(1)} />

      {design.showTestimonial && <TestimonialSection testimonial={design.testimonial} accent={accent} />}

      <GallerySection images={images} />

      {/* WHY GENIEX */}
      <section className="border-t border-line/70">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <SectionHeading eyebrow="Why GenieX" title="Other brands sell hardware. We deliver living." />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {(content.whyGenieX || DEFAULT_WHY).map((w) => (
              <div key={w.title} className="rounded-2xl border border-line p-7 bg-[var(--gx-surface)]/40 hover:border-gold/40 transition-colors">
                <div className={`w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center ${accent.icon} mb-5`}>
                  <I name={w.icon} className="w-4 h-4" />
                </div>
                <h3 className="font-display text-lg tracking-tight">{w.title}</h3>
                <p className="text-dim text-[13px] mt-3 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {content.faqs?.length > 0 && (
        <section className="border-t border-line/70 bg-[var(--gx-black)]">
          <div className="max-w-[980px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
            <SectionHeading eyebrow="Questions" title="Frequently asked." />
            <div className="mt-14 divide-y divide-line/60 border-y border-line/60">
              {content.faqs.map((f, i) => (
                <FaqRow key={i} q={f.q} a={f.a} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SHOP */}
      {kind === "products" ? (
        <SingleProductCTA
          shopSlug={PRODUCT_SHOP_MAP[slug]?.slug}
          fallbackCategory={PRODUCT_SHOP_MAP[slug]?.category}
        />
      ) : (
        <RecommendedProductsStrip categories={SOLUTION_SHOP_CATEGORIES[slug] || []} />
      )}

      {/* FINAL CTA */}
      <section className="relative border-t border-line/70">
        <div className="absolute inset-0 pointer-events-none" style={{ background: accent.radial }} />
        <div className="relative max-w-[1100px] mx-auto px-5 lg:px-10 py-20 lg:py-28 text-center">
          <p className="eyebrow mb-4">Begin Your Journey</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-[22ch] mx-auto">
            Let&apos;s design <span className="text-gold">{content.title.split("—")[0].trim()}</span> for your home.
          </h2>
          <p className="lead mt-7 max-w-[52ch] mx-auto">
            A senior GenieX advisor will personally walk you through a private demo, pricing in ₹, and a tailored plan for your home.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
            <button onClick={onBook} data-testid="cta-book" className="btn-gold">
              Book a Consultation <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href={`https://wa.me/${BRAND.phoneRaw.replace("+", "")}?text=${encodeURIComponent(`Hi GenieX, I'd like to enquire about ${content.title}.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp"
              className="btn-outline"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
            <Link to="/dynamic-series" data-testid="cta-explore-ds" className="btn-outline">
              Explore Dynamic Series
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

const DEFAULT_WHY = [
  { icon: "BadgeCheck", title: "Curated products", body: "Hand-picked from global luxury suppliers." },
  { icon: "HardHat", title: "White-glove install", body: "Zero damage to your interiors — in writing." },
  { icon: "ShieldCheck", title: "5-year warranty", body: "Same-week service across Telangana & Andhra." },
  { icon: "Sparkles", title: "Apple-grade design", body: "Beautiful from across the room. Or it doesn't ship." },
];

function FaqRow({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <button onClick={() => setOpen((v) => !v)} className="w-full text-left py-6 group" data-testid={`faq-${slugify(q)}`}>
      <div className="flex items-center justify-between gap-6">
        <span className="font-display text-lg sm:text-xl tracking-tight text-white">{q}</span>
        <ChevronDown className={`w-4 h-4 text-gold transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <p className="text-dim text-[14.5px] leading-relaxed pt-4 max-w-[64ch]">{a}</p>
        </div>
      </div>
    </button>
  );
}
